import React from 'react';
import { NavLink } from 'react-router-dom';
import { isProdEnv } from '../utils/environment';
import { useAuth } from '../contexts/AuthContext';
import { canOpen } from '../auth/roles';
import styles from './Navigation.module.scss';

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

// Links a person's roles cannot open are hidden (auth/roles.ts), and a
// section with no visible link is dropped.
const SECTIONS: NavSection[] = [
  {
    title: 'Main',
    // Highlights & Condensed Matches — nav hidden everywhere now that VODs
    // covers it; the route stays live at /vod-highlights for existing
    // bookmarks and Slack alert links.
    items: [
      { to: '/live', label: 'Live & Upcoming Matches' },
      { to: '/matches', label: 'Matches List' },
      { to: '/vods', label: 'VODs' },
    ],
  },
  {
    title: 'Management',
    items: [
      { to: '/tournaments', label: 'Tournaments' },
      { to: '/players', label: 'Players' },
      { to: '/teams', label: 'Teams' },
      // Cup Teams — nav hidden in production until launch; the route stays
      // live at /cup-teams.
      ...(isProdEnv() ? [] : [{ to: '/cup-teams', label: 'Cup Teams' }]),
      { to: '/courts', label: 'Complexes/Courts' },
      { to: '/feed-mapping', label: 'Feed Mapping' },
      { to: '/feeds', label: 'Feeds' },
      { to: '/series', label: 'Series' },
      { to: '/artwork-templates', label: 'Artwork Templates' },
    ],
  },
  {
    title: 'Site Content',
    items: [
      { to: '/content', label: 'Placements', end: true },
      { to: '/content/widgets', label: 'Widget Library' },
      { to: '/content/articles', label: 'Articles' },
      { to: '/content/navigation', label: 'Navigation' },
    ],
  },
  {
    title: 'Subscriptions & Access',
    items: [
      { to: '/mvpd-providers', label: 'MVPD Providers' },
      { to: '/promotions', label: 'Promotions' },
    ],
  },
  {
    title: 'Administration',
    items: [{ to: '/users', label: 'Users' }],
  },
];

const Navigation: React.FC = () => {
  const { roles } = useAuth();

  return (
    <nav className={styles.navContainer}>
      {SECTIONS.map((section) => {
        const items = section.items.filter((item) => canOpen(roles, item.to));
        if (items.length === 0) return null;
        return (
          <div key={section.title} className={styles.navSection}>
            <h3 className={styles.navSectionTitle}>{section.title}</h3>
            <ul className={styles.navList}>
              {items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) => isActive ? `${styles.navLink} ${styles.active}` : styles.navLink}
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </nav>
  );
};

export default Navigation;
